import { useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { CONTRACT_ADDRESS, CONTRACT_ABI, CHAIN_ID, weiToEth, ethToWei, oddsToBps } from '../lib/contract';
import { parseEther } from 'viem';

// Hook to read contract stats
export function useContractStats() {
  const { data, isLoading, refetch } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'getStats',
    chainId: CHAIN_ID, // Base Mainnet (8453)
  });

  return {
    totalBets: data ? Number(data[0]) : 0,
    totalVolume: data ? weiToEth(data[1]) : '0',
    totalFees: data ? weiToEth(data[2]) : '0',
    isLoading,
    refetch,
  };
}

// Hook to read a bet by ID
export function useContractBet(betId) {
  const { data, isLoading, refetch } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'getBet',
    args: [BigInt(betId || 0)],
    chainId: CHAIN_ID,
    query: {
      enabled: !!betId && betId > 0,
    },
  });

  return {
    bet: data ? {
      id: Number(data.id),
      creator: data.creator,
      taker: data.taker,
      description: data.description,
      creatorStake: weiToEth(data.creatorStake),
      takerStake: weiToEth(data.takerStake),
      oddsBps: Number(data.oddsBps),
      status: Number(data.status),
      outcome: Number(data.outcome),
      createdAt: Number(data.createdAt),
      resolvedAt: Number(data.resolvedAt),
    } : null,
    isLoading,
    refetch,
  };
}

// Hook to get user's bet IDs
export function useUserBets(address) {
  const { data, isLoading, refetch } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'getUserBets',
    args: [address],
    chainId: CHAIN_ID,
    query: {
      enabled: !!address,
    },
  });

  return {
    betIds: data ? data.map(id => Number(id)) : [],
    isLoading,
    refetch,
  };
}

// Hook to create a bet
export function useCreateBet() {
  const { writeContract, data: hash, isPending, error } = useWriteContract();
  
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  });

  const createBet = async (description, odds, stakeEth) => {
    const oddsBps = oddsToBps(odds);
    await writeContract({
      address: CONTRACT_ADDRESS,
      abi: CONTRACT_ABI,
      functionName: 'createBet',
      args: [description, BigInt(oddsBps)],
      value: parseEther(stakeEth.toString()),
      chainId: CHAIN_ID,
    });
  };

  return {
    createBet,
    hash,
    isPending,
    isConfirming,
    isSuccess,
    error,
  };
}

// Hook to accept a bet
export function useAcceptBet() {
  const { writeContract, data: hash, isPending, error } = useWriteContract();
  
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  });

  const acceptBet = async (betId, stakeEth) => {
    await writeContract({
      address: CONTRACT_ADDRESS,
      abi: CONTRACT_ABI,
      functionName: 'acceptBet',
      args: [BigInt(betId)],
      value: parseEther(stakeEth.toString()),
      chainId: CHAIN_ID,
    });
  };

  return {
    acceptBet,
    hash,
    isPending,
    isConfirming,
    isSuccess,
    error,
  };
}

// Hook to cancel a bet
export function useCancelBet() {
  const { writeContract, data: hash, isPending, error } = useWriteContract();
  
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  });

  const cancelBet = async (betId) => {
    await writeContract({
      address: CONTRACT_ADDRESS,
      abi: CONTRACT_ABI,
      functionName: 'cancelBet',
      args: [BigInt(betId)],
      chainId: CHAIN_ID,
    });
  };

  return {
    cancelBet,
    hash,
    isPending,
    isConfirming,
    isSuccess,
    error,
  };
}

// Export contract details for direct use
export { CONTRACT_ADDRESS, CONTRACT_ABI, CHAIN_ID };
